import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useDraftBlockers, useInterestRateText, useReceivingNotice } from "@/i18n/useInvoiceCreateText";
import { useInvoiceStatusLabel } from "@/i18n/useInvoiceStatusLabel";
import { CustomerPicker } from "@/components/invoiceGeneration/CustomerPicker";
import { LineItemsTable } from "@/components/invoiceGeneration/LineItemsTable";
import { InvoicePreview } from "@/components/invoiceGeneration/InvoicePreview";
import { PaymentMethodField } from "@/components/invoiceGeneration/PaymentMethodField";
import { ReceivingAccountField } from "@/components/invoiceGeneration/ReceivingAccountField";
import { BankgiroReplacementNotice } from "@/components/invoiceGeneration/BankgiroReplacementNotice";
import {
  invoiceProvider, applyVat, computeTotals, decideVat, addDays, money, formatMoneyForCurrency,
  interestRateOptions, FREE_TEXT_MAX_LENGTH,
  freshDraft, emptyLine, baseOnExistingInvoice, baseOnUploadedInvoice, classifiedLineDescriptions,
  checkDraftIssueReadiness, isSellerComplete, needsManualBankAccount,
  sellerPaidInto, changeReceivingAccount, carryOverReceivingAccount,
  draftForEditing, draftEditSignature, isDraftEditError,
  applyCustomerSelection,
} from "@/services/invoiceGeneration";
import type {
  Article, Customer, ReceivableDraft, Receivable, LineItem, NewCustomerInput, Seller, SellerProfileUpdate, UploadedInvoice,
  ManualBankAccountInput, DraftEditErrorKind, EditingDraft, DocumentLanguage, InvoiceCurrency,
} from "@/services/invoiceGeneration";
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { formatAmount } from '@financialharmony/ui';

// A Select item's value must be a non-empty string, so "no dröjsmålsränta on this invoice"
// needs a sentinel rather than "" — it maps to interestRatePercent: null on the way out.
const OMIT_INTEREST_RATE = "none";

/**
 * Drops any line id from a per-line Set that no longer belongs to a live line.
 *
 * Returns the previous Set by reference when nothing needs dropping, so React bails out of
 * the re-render instead of looping — this runs inside an effect that depends on draft.lines.
 */
const pruneToLiveIds = (
  setIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  liveIds: Set<string>,
) => setIds((prev) => {
  const stale = [...prev].filter((id) => !liveIds.has(id));
  if (stale.length === 0) return prev;
  const next = new Set(prev);
  stale.forEach((id) => next.delete(id));
  return next;
});
/**
 * Deliberately a local array, duplicated in apps/client's own generator — a
 * nine-item static list of integers does not justify a shared package, and both
 * apps are free to present it differently.
 */
const PAYMENT_TERMS_DAY_OPTIONS = [0, 10, 14, 15, 20, 30, 45, 60, 90];

interface EditData { seller: Seller; customers: Customer[]; articles: Article[] }

export default function CreateInvoice() {
  const navigate = useNavigate();
  const t = useTranslations("consultantInvoiceCreate");
  const tCommon = useTranslations("common");
  // Loaders below run from effects and callbacks; reading the translator through a ref keeps them
  // from re-running (and refetching) whenever the translator's identity changes.
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; });
  const statusLabel = useInvoiceStatusLabel();
  const interestText = useInterestRateText();
  const localizeBlockers = useDraftBlockers();
  const localizeReceivingNotice = useReceivingNotice();
  // `?draft=<id>` opens a saved draft for editing (FIN-829) instead of the start step.
  const [searchParams] = useSearchParams();
  const editDraftId = searchParams.get("draft");

  // The three start-step action cards render instantly; only the "Recent
  // invoices" list and "From PDF" dropdown load in behind their own skeletons.
  const [invoices, setInvoices] = useState<Receivable[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedInvoice[]>([]);
  const [uploadedLoading, setUploadedLoading] = useState(true);
  const [uploadedError, setUploadedError] = useState(false);
  const [listsReloadToken, setListsReloadToken] = useState(0);
  const reloadLists = () => setListsReloadToken((n) => n + 1);

  useEffect(() => {
    let alive = true;
    setInvoicesLoading(true);
    setInvoicesError(false);
    invoiceProvider.listReceivables()
      .then((invs) => { if (alive) setInvoices(invs); })
      .catch(() => { if (alive) setInvoicesError(true); })
      .finally(() => { if (alive) setInvoicesLoading(false); });
    return () => { alive = false; };
  }, [listsReloadToken]);

  useEffect(() => {
    let alive = true;
    setUploadedLoading(true);
    setUploadedError(false);
    invoiceProvider.listUploadedInvoices()
      .then((us) => { if (alive) setUploaded(us); })
      .catch(() => { if (alive) setUploadedError(true); })
      .finally(() => { if (alive) setUploadedLoading(false); });
    return () => { alive = false; };
  }, [listsReloadToken]);

  // Seller/customers/articles are only needed once the user actually starts
  // editing a draft — deferred and fetched (once) on the first "start" action.
  const [editData, setEditData] = useState<EditData | null>(null);
  const [editDataLoading, setEditDataLoading] = useState(false);
  const [editDataError, setEditDataError] = useState<string | null>(null);
  const editDataPromiseRef = useRef<Promise<EditData> | null>(null);

  // Stable (no deps — refs and setters only), so the draft-loading effect below can list it.
  const loadEditData = useCallback((): Promise<EditData> => {
    if (editDataPromiseRef.current) return editDataPromiseRef.current;
    setEditDataLoading(true);
    setEditDataError(null);
    const p = Promise.all([
      invoiceProvider.getSeller(), invoiceProvider.listCustomers(), invoiceProvider.listArticles(),
    ]).then(([s, cs, as]): EditData => {
      const result = { seller: s, customers: cs, articles: as };
      setEditData(result);
      return result;
    }).catch((err: unknown) => {
      editDataPromiseRef.current = null; // allow retry
      setEditDataError(userFacingErrorMessage(err, tRef.current("errors.companyData")));
      throw err;
    }).finally(() => setEditDataLoading(false));
    editDataPromiseRef.current = p;
    return p;
  }, []);

  const seller = editData?.seller ?? null;
  const customers = useMemo(() => editData?.customers ?? [], [editData]);
  const articles = editData?.articles ?? [];

  const [draft, setDraft] = useState<ReceivableDraft>(freshDraft);
  const [mode, setMode] = useState<"choose" | "edit">(editDraftId ? "edit" : "choose");
  const [editing, setEditing] = useState<EditingDraft | null>(null);
  const [editRefusal, setEditRefusal] = useState<DraftEditErrorKind | null>(null);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const [editReloadToken, setEditReloadToken] = useState(0);
  // The loaded draft's signature, to tell whether leaving would discard edits (EC10).
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);

  // Real LLM VAT classification (#287 follow-up — no more keyword guessing,
  // and no more silent fallback on failure: classifyLineVat now throws rather
  // than resolving with a guessed category, since VAT isn't user-editable on
  // an invoice — a failure must block issuing and be visibly retryable, not
  // quietly become a possibly-wrong 25%). Debounced per line id so a fast
  // typer doesn't fire one LLM call per keystroke; a line only re-classifies
  // once its description settles.
  const [classifyingLineIds, setClassifyingLineIds] = useState<Set<string>>(new Set());
  const [classifyErrorLineIds, setClassifyErrorLineIds] = useState<Set<string>>(new Set());
  const classifyTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastClassifiedDesc = useRef<Record<string, string>>({});
  // Tracks which description the currently-pending timer targets, separate from
  // lastClassifiedDesc (only set once classification actually starts). Without
  // this, any unrelated setDraft (e.g. createCustomer's applyVat) produces a new
  // draft.lines reference, re-running the effect below and unconditionally
  // resetting the 600ms timer for every still-unclassified line — under enough
  // churn the debounce can be perpetually restarted and the LLM call never fires.
  const pendingClassifyDesc = useRef<Record<string, string>>({});
  // Mirrors the live lines for classifyLine's async guards below. Its callbacks run
  // long after the closure was created, and the `.catch` has no setDraft updater to read
  // the current line out of the way `.then` does.
  const linesRef = useRef(draft.lines);

  const classifyLine = useCallback((lineId: string, desc: string) => {
    // Permanent, low-volume breadcrumb (#287 follow-up) — the "Classifying VAT…"
    // E2E assertion failed intermittently in CI with zero classifyLineVat network
    // activity; tracked down to the description-caching path in pickArticle below
    // bypassing this function entirely (full writeup in git history on
    // e2e/tests/invoice-generation.spec.ts, removed by issue #673), not a debounce
    // bug. This line, paired with the
    // "scheduling" log below, is left in place since default CI logs don't
    // capture browser console output — a future regression in this specific area
    // (a known historically-flaky spot) is diagnosable straight from the CI log.
    console.debug(`[classifyDebounce] firing line=${lineId} desc=${JSON.stringify(desc)} t=${Date.now()} visibility=${document.visibilityState} focused=${document.hasFocus()}`);
    lastClassifiedDesc.current[lineId] = desc;
    setClassifyErrorLineIds((prev) => { if (!prev.has(lineId)) return prev; const next = new Set(prev); next.delete(lineId); return next; });
    setClassifyingLineIds((prev) => new Set(prev).add(lineId));
    invoiceProvider.classifyLineVat(desc)
      .then((result) => {
        setDraft((d) => {
          const current = d.lines.find((l) => l.id === lineId);
          // Stale-response guard: description moved on since this call fired.
          if (!current || current.description.trim() !== desc) return d;
          const cust = customers.find((c) => c.id === d.customerId) ?? null;
          const patched = d.lines.map((l) => (l.id === lineId ? { ...l, vatCategory: result.category } : l));
          return { ...d, lines: applyVat(cust, patched, d.documentLanguage) };
        });
      })
      .catch(() => {
        // Stale-response guard, mirroring `.then`'s above. A rejection for a description
        // the line no longer holds must not flag it: the LLM call armed for a half-typed
        // description can land after the user has completed it into a known article, and
        // this Set is what disables Save draft (FIN-718).
        const current = linesRef.current.find((l) => l.id === lineId);
        if (!current || current.description.trim() !== desc) return;
        setClassifyErrorLineIds((prev) => new Set(prev).add(lineId));
      })
      .finally(() => setClassifyingLineIds((prev) => { const next = new Set(prev); next.delete(lineId); return next; }));
  }, [customers]);

  function retryClassifyLine(lineId: string) {
    const line = draft.lines.find((l) => l.id === lineId);
    const desc = line?.description.trim();
    if (!desc) return;
    clearTimeout(classifyTimers.current[lineId]);
    classifyLine(lineId, desc);
  }

  useEffect(() => {
    linesRef.current = draft.lines;
    const liveIds = new Set(draft.lines.map((l) => l.id));
    Object.keys(classifyTimers.current).forEach((id) => {
      if (!liveIds.has(id)) { clearTimeout(classifyTimers.current[id]); delete classifyTimers.current[id]; delete lastClassifiedDesc.current[id]; delete pendingClassifyDesc.current[id]; }
    });
    // The two Sets below are keyed by line id and must be pruned here too, not just the
    // refs above: a failed classification whose line is later removed (or replaced
    // wholesale by freshDraft() when a new invoice is started) otherwise leaves its id in
    // classifyErrorLineIds forever. `missing` then permanently reports "VAT could not be
    // determined for one or more lines" and disables issuing, while no per-line Retry
    // renders — no live line carries that id — so the sender is blocked by an error about
    // lines that do not exist, with no way out but a page reload.
    pruneToLiveIds(setClassifyErrorLineIds, liveIds);
    pruneToLiveIds(setClassifyingLineIds, liveIds);
    draft.lines.forEach((line) => {
      const desc = line.description.trim();
      if (!desc || lastClassifiedDesc.current[line.id] === desc) return;
      if (pendingClassifyDesc.current[line.id] === desc) return;
      clearTimeout(classifyTimers.current[line.id]);
      pendingClassifyDesc.current[line.id] = desc;
      // Paired with the "firing" log in classifyLine — see comment there.
      console.debug(`[classifyDebounce] scheduling line=${line.id} desc=${JSON.stringify(desc)} t=${Date.now()}`);
      classifyTimers.current[line.id] = setTimeout(() => classifyLine(line.id, desc), 600);
    });
  }, [draft.lines, classifyLine]);

  // Unmount-only: the effect above deliberately does NOT clear timers in a cleanup — it
  // re-runs on every draft.lines change, and clearing there would restart the debounce
  // forever and never fire. But a form left mid-typing (the user navigates away, or the
  // draft list replaces it) must not still send its LLM call minutes later, into a
  // component that is gone.
  useEffect(() => () => {
    Object.values(classifyTimers.current).forEach(clearTimeout);
    classifyTimers.current = {};
  }, []);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoNudgeDismissed, setLogoNudgeDismissed] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const onLogoFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoUploading(true);
    try {
      const url = await invoiceProvider.uploadLogo(f);
      setLogoUrl(url);
    } catch {
      toast.error(t("errors.logo"));
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const sellerWithLogo = useMemo<Seller | null>(
    () => (seller ? { ...seller, logoUrl: logoUrl ?? seller.logoUrl ?? null } : null),
    [seller, logoUrl],
  );
  // The seller as this invoice presents it: the chosen (or default) account's details only
  // (FIN-283). The Payment method field and the preview both read this.
  const effectiveSeller = sellerPaidInto(sellerWithLogo, draft.receivingBankAccountId);
  const sellerComplete = isSellerComplete(effectiveSeller);
  const needsManualAccount = needsManualBankAccount(sellerWithLogo);

  // The prefill lands once, when the seller profile arrives, and never overwrites a
  // value the sender has already touched — an autofill that fights the user is worse
  // than no autofill.
  // Keyed on the draft, not on the component: a sender who creates a second invoice
  // without leaving the page gets a fresh draft, and prefilling only once per mount left
  // that one with no dröjsmålsränta at all — after showing them a prefilled rate on the
  // first (caught end-to-end, not in review). Still never overwrites a value already set,
  // so a draft based on a previous invoice keeps that invoice's rate and a sender who
  // cleared the field is not fought.
  const interestPrefilledForDraft = useRef<string | null>(null);
  useEffect(() => {
    // A saved draft's own interest rate is authoritative — null there means the sender
    // chose "omit", not "not chosen yet" (FIN-829 AC3).
    if (editDraftId) return;
    if (!seller || interestPrefilledForDraft.current === draft.id) return;
    interestPrefilledForDraft.current = draft.id;
    setDraft((d) => (d.interestRatePercent == null ? { ...d, interestRatePercent: seller.defaultInterestRatePercent } : d));
  }, [seller, draft.id, editDraftId]);

  const maxInterestRate = seller?.defaultInterestRatePercent ?? null;
  const interestRateError = interestText.problem(draft.interestRatePercent, maxInterestRate);

  const customer = useMemo(() => customers.find((c) => c.id === draft.customerId) ?? null, [customers, draft.customerId]);
  // FIN-1580: currency drives the rounding rule itself (SEK: whole-krona öresavrundning;
  // every other currency: ordinary 2-decimal rounding) -- not just which symbol is shown.
  const totals = useMemo(() => computeTotals(draft.lines, draft.currency), [draft.lines, draft.currency]);
  const dueDate = addDays(draft.invoiceDate, draft.paymentTermsDays);
  const vat = customer ? decideVat(customer, "", undefined, draft.documentLanguage) : null;
  const hasBillableLine = draft.lines.some((l) => l.description.trim() && l.quantity * l.unitPrice > 0);
  const showPreview = !!customer && hasBillableLine;

  const { ready: canIssue, missing } = useMemo(
    () => checkDraftIssueReadiness({
      seller: sellerWithLogo,
      customer,
      draft,
      hasBillableLine,
      classifyingLineCount: classifyingLineIds.size,
      classifyErrorLineCount: classifyErrorLineIds.size,
    }),
    [sellerWithLogo, customer, draft, hasBillableLine, classifyingLineIds, classifyErrorLineIds],
  );

  // FIN-280 AC2: display-only signal for the VAT cell — true whenever a line's current
  // (trimmed) description doesn't match the description it was last classified against,
  // whether that's because it was just typed or because it's a freshly-extracted line
  // that has never been classified at all. Deliberately separate from classifyingLineIds:
  // it must never gate Save/Issue (missing/canIssue above don't read it), only drive the
  // VAT cell's loading indicator so no line ever shows a confident-looking rate before its
  // current description has actually been classified — including the up-to-600ms window
  // before the classify debounce fires.
  //
  // Derived in an effect rather than during render, for two reasons that pull the same way:
  // reading lastClassifiedDesc.current during render is not legal under concurrent React
  // (react-hooks/refs), and a useMemo keyed on [draft.lines] would go stale anyway —
  // classifyLine mutates that ref synchronously without itself changing draft.lines, so the
  // memo would keep returning a pre-classification Set and the line would read as "pending"
  // forever. Depending on classifyingLineIds/classifyErrorLineIds is what re-runs this when
  // classification starts and settles, which is exactly when the ref's value changes.
  const [pendingLineIds, setPendingLineIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const next = new Set<string>();
    for (const line of draft.lines) {
      const desc = line.description.trim();
      if (desc && lastClassifiedDesc.current[line.id] !== desc) next.add(line.id);
    }
    setPendingLineIds((prev) =>
      prev.size === next.size && [...next].every((id) => prev.has(id)) ? prev : next);
  }, [draft.lines, classifyingLineIds, classifyErrorLineIds]);

  const setLines = (lines: LineItem[], cust: Customer | null = customer) => setDraft((d) => ({ ...d, lines: applyVat(cust, lines, d.documentLanguage) }));
  const field = (patch: Partial<ReceivableDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const [receivingNotice, setReceivingNotice] = useState<string | null>(null);
  const chooseReceivingAccount = (bankAccountId: string) => {
    const { patch, notice } = changeReceivingAccount(sellerWithLogo, draft, bankAccountId);
    field(patch);
    setReceivingNotice(notice);
  };
  const saveBankgiro = async (bankAccountId: string, typed: string) => {
    const updated = await invoiceProvider.setBankgiro(bankAccountId, typed);
    setEditData((prev) => prev ? { ...prev, seller: updated } : prev);
    return updated;
  };
  const saveCompanyBankgiro = async (typed: string) => {
    const updated = await invoiceProvider.setCompanyBankgiro(typed);
    setEditData((prev) => prev ? { ...prev, seller: updated } : prev);
    return updated;
  };

  async function startBlank() {
    setMode("edit");
    setReceivingNotice(null);
    setDraft(freshDraft());
    await loadEditData().catch(() => {});
  }
  async function startFromInvoice(id: string) {
    setMode("edit");
    const src = invoices.find((i) => i.id === id); if (!src) return;
    const data = await loadEditData().catch(() => null);
    const c = data?.customers.find((x) => x.id === src.customerId) ?? null;
    const base = baseOnExistingInvoice(src, c);
    const carried = carryOverReceivingAccount(data?.seller ?? null, base.receivingBankAccountId);
    Object.assign(lastClassifiedDesc.current, classifiedLineDescriptions(base.lines));
    setReceivingNotice(carried.notice);
    setDraft({ ...base, receivingBankAccountId: carried.receivingBankAccountId });
  }
  async function startFromUploaded(id: string) {
    setMode("edit");
    const u = uploaded.find((x) => x.id === id); if (!u) return;
    const data = await loadEditData().catch(() => null);
    const c = data?.customers.find((x) => x.id === u.customerId) ?? null;
    setReceivingNotice(null);
    setDraft(baseOnUploadedInvoice(u, c));
  }

  useEffect(() => {
    if (!editDraftId) return;
    let alive = true;
    setEditLoadError(null);
    setEditRefusal(null);
    Promise.all([invoiceProvider.getDraftForEdit(editDraftId), loadEditData()])
      .then(([saved]) => {
        if (!alive) return;
        if (saved.status !== "draft" || saved.version == null) {
          setEditRefusal("no_longer_editable");
          return;
        }
        // Loaded lines already carry their VAT category, so none is reclassified on open.
        saved.lines.forEach((l) => { if (l.vatCategory !== undefined) lastClassifiedDesc.current[l.id] = l.description.trim(); });
        const loaded = draftForEditing(saved);
        setDraft(loaded);
        setLoadedSignature(draftEditSignature(loaded));
        setEditing({ id: saved.id, number: saved.number, version: saved.version });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isDraftEditError(err)) setEditRefusal(err.kind);
        else setEditLoadError(userFacingErrorMessage(err, tRef.current("errors.draftLoad")));
      });
    return () => { alive = false; };
  }, [editDraftId, editReloadToken, loadEditData]);

  const hasUnsavedEdits = !!editing && loadedSignature !== null && draftEditSignature(draft) !== loadedSignature;

  // A tab close or reload with unsaved edits asks the browser's own "leave site?" (EC10).
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedEdits]);

  let saveLabel = t("form.saveDraft");
  if (editing) saveLabel = issuing ? t("form.saving") : t("form.saveChanges");

  function leaveEditor() {
    if (hasUnsavedEdits) setConfirmDiscard(true);
    else navigate("/invoices");
  }

  function selectCustomer(id: string) {
    const c = customers.find((x) => x.id === id) ?? null;
    setDraft((d) => ({
      ...d,
      customerId: id,
      // FIN-1580: resolves the language/currency default from the customer only on this
      // draft's FIRST customer pick — a later customer change (on the same draft) never
      // overwrites a choice the user may have already made.
      ...applyCustomerSelection(d, c, d.customerId == null, applyVat),
    }));
  }
  async function createCustomer(input: NewCustomerInput) {
    const c = await invoiceProvider.createCustomer(input);
    setEditData((prev) => prev ? { ...prev, customers: [...prev.customers, c] } : prev);
    setDraft((d) => ({
      ...d,
      customerId: c.id,
      ...applyCustomerSelection(d, c, d.customerId == null, applyVat),
    }));
    toast.success(t("toast.customerAdded"));
  }
  function pickArticle(id: string, a: Article) {
    // The picked article already carries its own LLM-classified vat_category
    // (set once, at article creation — see ArticleService.findOrCreate) — use
    // it directly instead of firing a redundant classifyLineVat call for text
    // that's already been classified.
    lastClassifiedDesc.current[id] = a.name;
    // This line is now classified, so any earlier failure on it is stale — and
    // nothing else would ever clear it (FIN-763). `classifyLine` is the only
    // other place that does, and the assignment above is precisely what stops
    // the debounce effect below from ever scheduling it for this description.
    // Without this the form keeps reporting "VAT could not be determined" and
    // keeps saving disabled, which makes "retry or edit the description" —
    // the instruction the form itself gives — a dead end.
    setClassifyErrorLineIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // A debounce armed by the description the user typed before picking is
    // still pending; letting it fire would re-flag the line via classifyLine's
    // own catch, seconds after the pick appeared to fix it.
    clearTimeout(classifyTimers.current[id]);
    delete pendingClassifyDesc.current[id];
    setLines(draft.lines.map((l) => (l.id === id ? { ...l, articleId: a.id, description: a.name, unit: a.defaultUnit, unitPrice: a.defaultUnitPrice, vatCategory: a.vatCategory ?? null } : l)));
  }
  async function issue() {
    if (!canIssue || !customer) return;
    setIssuing(true);
    setIssueError(null);
    try {
      // Saves a DRAFT. Nothing is booked and no gap-free sequence number is consumed
      // until the user explicitly marks it sent from the invoice list (FIN-603 F1/F4).
      const rec = editing
        ? await invoiceProvider.updateDraft(editing.id, editing.version, draft, customer.name)
        : await invoiceProvider.saveDraft(draft, customer.name);
      toast.success(t("toast.draftSaved", { number: rec.number }), {
        description: t("toast.draftSavedBody", { amount: money(rec.toPay) }),
      });
      navigate("/invoices");
    } catch (err) {
      if (isDraftEditError(err)) {
        // EC1/EC2/EC3 — shown in place, with the user's edits still on screen.
        setEditRefusal(err.kind);
        return;
      }
      // Was previously uncaught (try/finally only) — a real failure here (e.g. a
      // VAT classification error at draft time) would silently re-enable the
      // button with zero user-visible feedback. Never leave a failure invisible.
      setIssueError(userFacingErrorMessage(err, t("errors.unknown")));
      toast.error(t("errors.saveDraftTitle"));
    } finally {
      setIssuing(false);
    }
  }

  // Which body the edit step shows — one at a time, in this priority order.
  let editView: "draft-load-error" | "refused" | "data-error" | "loading" | "form";
  if (editDraftId && editLoadError) editView = "draft-load-error";
  else if (editDraftId && !editing && editRefusal) editView = "refused";
  else if (!editData && editDataError) editView = "data-error";
  else if (!editData || (editDraftId && !editing)) editView = "loading";
  else editView = "form";

  return (
    <div className="p-6">
      <div className="mb-6">
        {editDraftId ? (
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{editing ? t("page.editTitleNumbered", { number: editing.number }) : t("page.editTitle")}</h1>
            <Badge variant="outline">{t("page.draftBadge")}</Badge>
          </div>
        ) : (
          <h1 className="text-2xl font-bold">{t("page.createTitle")}</h1>
        )}
        <p className="text-sm text-muted-foreground">
          {editDraftId ? t("page.editSubtitle") : t("page.createSubtitle")}
        </p>
      </div>

      {mode === "choose" ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <ChoiceCard title={t("choose.newTitle")} desc={t("choose.newDesc")} action={<Button size="sm" onClick={startBlank}>{t("choose.start")}</Button>} />
            <ChoiceCard
              title={t("choose.basedTitle")}
              desc={t("choose.basedDesc")}
              action={(
                <StartPicker
                  loading={invoicesLoading} failed={invoicesError} onRetry={reloadLists}
                  items={invoices.map((i) => ({ id: i.id, label: `${i.number} · ${i.customerName}` }))}
                  placeholder={t("choose.chooseInvoice")} emptyText={t("choose.noInvoices")} onPick={startFromInvoice}
                />
              )}
            />
            <ChoiceCard
              title={t("choose.pdfTitle")}
              desc={t("choose.pdfDesc")}
              action={(
                <StartPicker
                  loading={uploadedLoading} failed={uploadedError} onRetry={reloadLists}
                  items={uploaded.map((u) => ({ id: u.id, label: tCommon("documentPicker.option", { sender: u.vendor ?? tCommon("documentPicker.unknownSender"), amount: formatAmount(u.amount, u.currency) }) }))}
                  placeholder={t("choose.chooseDocument")} emptyText={t("choose.noImported")} onPick={startFromUploaded}
                />
              )}
            />
          </div>

          <RecentInvoices
            loading={invoicesLoading} failed={invoicesError} onRetry={reloadLists}
            invoices={invoices} statusLabel={statusLabel}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            {editDraftId ? (
              <Button variant="ghost" size="sm" onClick={leaveEditor}>{t("editor.backToInvoices")}</Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setMode("choose")}>{t("editor.changeStart")}</Button>
            )}
          </div>

          {editRefusal && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t(`refusal.${editRefusal}.title`)}</AlertTitle>
              <AlertDescription>{t(`refusal.${editRefusal}.body`)}</AlertDescription>
              <div className="mt-3 flex gap-2">
                {editRefusal === "changed_elsewhere" && (
                  <Button size="sm" onClick={() => setEditReloadToken((n) => n + 1)}>{t("editor.reloadLatest")}</Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => navigate("/invoices")}>{t("editor.backShort")}</Button>
              </div>
            </Alert>
          )}

          {editView === "draft-load-error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("editor.draftLoadFailedTitle")}</AlertTitle>
              <AlertDescription>{editLoadError}</AlertDescription>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => setEditReloadToken((n) => n + 1)}>
                {t("editor.tryAgain")}
              </Button>
            </Alert>
          )}
          {editView === "data-error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("editor.companyLoadFailedTitle")}</AlertTitle>
              <AlertDescription>{editDataError}</AlertDescription>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => loadEditData()}>
                {t("editor.tryAgain")}
              </Button>
            </Alert>
          )}
          {editView === "loading" && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* form — mirrors the real field layout below: customer row, VAT note,
                  date/terms row, references row, line items, totals */}
              <div className="space-y-5 rounded-lg border bg-card p-5">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-3 w-2/3" />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
              {/* preview — same card shell as InvoicePreview, with header/customer/table/totals regions */}
              <div className="relative mx-auto aspect-[1/1.414] w-full max-w-[620px] rounded-lg border bg-card p-6 md:p-7">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-9 w-9" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="mt-6 flex items-start justify-between">
                  <Skeleton className="h-10 w-40" />
                  <Skeleton className="h-8 w-28" />
                </div>
                <Skeleton className="mt-6 h-32 w-full" />
                <Skeleton className="mt-4 ml-auto h-16 w-full max-w-xs" />
              </div>
            </div>
          )}
          {editView === "form" && (
          <>
          {!sellerComplete && seller && (
            <CompanyInfoGate
              seller={seller}
              onSave={async (fields) => {
                const updated = await invoiceProvider.updateSellerProfile(fields);
                setEditData((prev) => prev ? { ...prev, seller: updated } : prev);
              }}
            />
          )}

          {effectiveSeller?.bankgiroReplacementNotice && (
            <BankgiroReplacementNotice
              notice={effectiveSeller.bankgiroReplacementNotice}
              onDismiss={async (bankAccountId) => {
                const updated = await invoiceProvider.dismissBankgiroNotice(bankAccountId);
                setEditData((prev) => prev ? { ...prev, seller: updated } : prev);
              }}
            />
          )}

          {needsManualAccount && seller && (
            <BankAccountGate
              onSave={async (input) => {
                const updated = await invoiceProvider.createManualBankAccount(input);
                setEditData((prev) => prev ? { ...prev, seller: updated } : prev);
              }}
            />
          )}

          {sellerComplete && !effectiveSeller?.logoUrl && !logoNudgeDismissed && (
            <Alert>
              <AlertTitle>{t("logo.title")}</AlertTitle>
              <AlertDescription>
                {t("logo.body")}
                <div className="mt-2 flex gap-2">
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} />
                  <Button size="sm" variant="outline" disabled={logoUploading} onClick={() => logoInputRef.current?.click()}>
                    {logoUploading ? t("logo.uploading") : t("logo.upload")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setLogoNudgeDismissed(true)}>{t("logo.notNow")}</Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* form */}
            <div className="space-y-5 rounded-lg border bg-card p-5">
              <p className="text-xs text-muted-foreground">
                {t.rich("form.required", { star: (chunks) => <span className="text-destructive">{chunks}</span> })}
              </p>
              <CustomerPicker customers={customers} selectedId={draft.customerId} onSelect={selectCustomer} onCreate={createCustomer} />

              {customer && vat && (
                <p className="text-xs text-muted-foreground">
                  {t("form.vatLine", {
                    treatment: t(`form.vatTreatment.${vat.treatment}`),
                    country: customer.countryName,
                    hasReason: vat.reason ? "yes" : "no",
                    reason: vat.reason ?? "",
                  })}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">{t("form.invoiceDate")} <span className="text-destructive">*</span></Label><Input type="date" value={draft.invoiceDate} onChange={(e) => field({ invoiceDate: e.target.value })} /></div>
                <div>
                  <Label className="mb-1.5 block">{t("form.paymentTerms")}</Label>
                  <Select value={String(draft.paymentTermsDays)} onValueChange={(v) => field({ paymentTermsDays: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_TERMS_DAY_OPTIONS.map((days) => <SelectItem key={days} value={String(days)}>{days === 0 ? t("form.termsImmediate") : t("form.termsDays", { days })}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* FIN-1580: document language/currency — both always have a safe default
                  (sv/SE or en, SEK), so neither ever blocks issuing and neither gets a
                  required-asterisk (specs/invoice-document-i18n/screens.md, Option A). */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">{t("form.documentLanguage")}</Label>
                  <Select value={draft.documentLanguage} onValueChange={(v) => field({ documentLanguage: v as DocumentLanguage })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sv">{t("form.documentLanguageSv")}</SelectItem>
                      <SelectItem value="en">{t("form.documentLanguageEn")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">{t("form.currency")}</Label>
                  <Select value={draft.currency} onValueChange={(v) => field({ currency: v as InvoiceCurrency })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEK">SEK</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">{t("form.yourReference")}</Label><Input aria-label={t("form.yourReference")} value={draft.theirReference} onChange={(e) => field({ theirReference: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">{t("form.ourReference")}</Label><Input aria-label={t("form.ourReference")} value={draft.ourReference} onChange={(e) => field({ ourReference: e.target.value })} /></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">{t("form.interest")}</Label>
                  {/* Picked from a ladder, not typed: the rate is a legal term the customer
                      can be held to, and the statutory option is the only one that may carry
                      "enligt räntelagen" — see interestRateOptions. A rate that arrived from
                      elsewhere (MCP, a copied invoice) still appears, so nothing is silently
                      rewritten; interestRateError below is what reports it if out of range. */}
                  <Select
                    value={draft.interestRatePercent == null ? OMIT_INTEREST_RATE : String(draft.interestRatePercent)}
                    onValueChange={(v) => field({ interestRatePercent: v === OMIT_INTEREST_RATE ? null : Number(v) })}
                  >
                    <SelectTrigger aria-label={t("form.interest")}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={OMIT_INTEREST_RATE}>{t("form.omitInterest")}</SelectItem>
                      {interestRateOptions(maxInterestRate, draft.interestRatePercent).map((rate) => (
                        <SelectItem key={rate} value={String(rate)}>
                          {interestText.optionLabel(rate, maxInterestRate)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {interestRateError
                    ? <p className="mt-1 text-xs text-destructive">{interestRateError}</p>
                    : <p className="mt-1 text-xs text-muted-foreground">{t("form.omitInterestHint")}</p>}
                </div>
                <ReceivingAccountField
                  seller={sellerWithLogo}
                  value={draft.receivingBankAccountId}
                  onChange={chooseReceivingAccount}
                  notice={localizeReceivingNotice(receivingNotice)}
                />
                <PaymentMethodField
                  seller={effectiveSeller}
                  value={draft.paymentMethod ?? null}
                  onChange={(method) => field({ paymentMethod: method })}
                  onSaveBankgiro={saveBankgiro}
                  onSaveCompanyBankgiro={saveCompanyBankgiro}
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <Label>{t("form.message")}</Label>
                  <span className="text-xs text-muted-foreground">{draft.freeText.length}/{FREE_TEXT_MAX_LENGTH}</span>
                </div>
                <Textarea
                  aria-label={t("form.message")}
                  maxLength={FREE_TEXT_MAX_LENGTH}
                  rows={3}
                  value={draft.freeText}
                  onChange={(e) => field({ freeText: e.target.value })}
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between"><Label>{t("form.lines")} <span className="text-destructive">*</span></Label><span className="text-xs text-muted-foreground">{t("form.vatAutomatic")}</span></div>
                <LineItemsTable
                  lines={draft.lines} articles={articles}
                  onChange={(id, patch) => setLines(draft.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)))}
                  onAdd={() => setLines([...draft.lines, emptyLine()])}
                  onRemove={(id) => setLines(draft.lines.filter((l) => l.id !== id))}
                  onPickArticle={pickArticle}
                  classifyingLineIds={classifyingLineIds}
                  pendingLineIds={pendingLineIds}
                  classifyErrorLineIds={classifyErrorLineIds}
                  onRetryClassify={retryClassifyLine}
                  // EC13: only a domestic invoice may have its rate set by hand — a
                  // cross-border one legally must carry 0% and reverse-charge/export
                  // wording. The backend refuses it either way; this stops offering it.
                  canOverrideVat={vat?.treatment === "domestic"}
                />
              </div>

              <div className="space-y-1 rounded-md border bg-muted/40 p-4 text-sm">
                <Row k={t("totals.net")} v={formatMoneyForCurrency(totals.net, draft.currency)} />
                {totals.vatGroups.map((g) => <Row key={g.rate} k={t("totals.vat", { rate: String(g.rate) })} v={formatMoneyForCurrency(g.vat, draft.currency)} />)}
                {totals.rounding !== 0 && <Row k={t("totals.rounding")} v={formatMoneyForCurrency(totals.rounding, draft.currency)} />}
                <div className="flex justify-between border-t pt-2 text-base font-bold"><span>{t("totals.total")}</span><span>{formatMoneyForCurrency(totals.toPay, draft.currency)}</span></div>
              </div>

            </div>

            {/* preview + issue */}
            <div className="space-y-4">
              {showPreview ? (
                <div className="mx-auto w-full max-w-[620px]">
                  <InvoicePreview
                    seller={effectiveSeller} customer={customer} lines={draft.lines} totals={totals}
                    invoiceDate={draft.invoiceDate} dueDate={dueDate}
                    ourReference={draft.ourReference} theirReference={draft.theirReference}
                    number={editing?.number ?? null} ocr={editing?.number ?? null} vatWording={vat?.requiredWording ?? null}
                    interestRatePercent={draft.interestRatePercent}
                    freeText={draft.freeText}
                    paymentMethod={draft.paymentMethod ?? effectiveSeller?.resolvedPaymentMethod}
                    documentLanguage={draft.documentLanguage}
                    currency={draft.currency}
                  />
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                  {t("form.previewHint")}
                </div>
              )}

              {missing.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("form.missingTitle")}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4">
                      {localizeBlockers(missing, draft.interestRatePercent, maxInterestRate).map((text, i) => <li key={missing[i]}>{text}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Inline rather than a toast, and it stays that way now that toasts actually
                  render (FIN-459): a refusal the sender has to ACT on — shorten the message,
                  split the lines — belongs next to the button that refused, not in something
                  that fades. apps/client surfaces the same failure the same way. */}
              {issueError && (
                <Alert variant="destructive" role="alert">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("errors.saveDraftTitle")}</AlertTitle>
                  <AlertDescription>{issueError}</AlertDescription>
                </Alert>
              )}

              {/* No confirm dialog: saving a draft commits nothing — it books nothing,
                  consumes no gap-free number, and stays editable and deletable. The
                  irreversible step is "Mark as sent" in the invoice list, which has its
                  own confirm (FIN-603 F1/F4). */}
              {editing && (
                <Button size="lg" variant="outline" className="mr-2" disabled={issuing} onClick={leaveEditor}>{t("form.cancel")}</Button>
              )}
              <Button size="lg" disabled={!canIssue || issuing || editRefusal === "no_longer_editable" || editRefusal === "gone"} onClick={issue}>
                {issuing ? <Loader2 className="mr-2 animate-spin" /> : null}{saveLabel}
              </Button>            </div>
          </div>
          </>
          )}
        </div>
      )}
      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("discard.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("discard.body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("discard.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/invoices")}>{t("discard.discard")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StartPicker({ loading, failed, onRetry, items, placeholder, emptyText, onPick }: {
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  items: { id: string; label: string }[];
  placeholder: string;
  emptyText: string;
  onPick: (id: string) => void;
}) {
  if (loading) return <Skeleton className="h-9 w-full" />;
  if (failed) return <RetryInline onRetry={onRetry} />;
  if (items.length === 0) return <p className="text-xs text-muted-foreground">{emptyText}</p>;
  return (
    <Select onValueChange={onPick}>
      <SelectTrigger className="h-9"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function RecentInvoices({ loading, failed, onRetry, invoices, statusLabel }: {
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  invoices: Receivable[];
  statusLabel: (status: string) => string;
}) {
  const t = useTranslations("consultantInvoiceCreate.choose");
  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border bg-card p-4 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (failed) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <RetryInline label={t("loadFailed")} onRetry={onRetry} />
      </div>
    );
  }
  if (invoices.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="border-b px-4 py-3 text-sm font-semibold">{t("recent")}</div>
      <table className="w-full text-sm">
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-2.5 font-medium">{inv.number}</td>
              <td className="px-4 py-2.5">{inv.customerName}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{statusLabel(inv.status)}</td>
              <td className="px-4 py-2.5 text-right font-medium">{money(inv.toPay)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChoiceCard({ title, desc, action }: { title: string; desc: string; action: ReactNode }) {
  return (
    <div className="flex flex-col justify-between rounded-lg border bg-card p-4">
      <div><p className="font-semibold">{title}</p><p className="mt-1 text-sm text-muted-foreground">{desc}</p></div>
      <div className="mt-4">{action}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}

function RetryInline({ label, onRetry }: { label?: string; onRetry: () => void }) {
  const t = useTranslations("consultantInvoiceCreate.choose");
  return (
    <p className="text-xs text-muted-foreground">
      {label ?? t("loadFailedShort")}{" "}
      <button type="button" onClick={onRetry} className="text-primary underline underline-offset-2">
        {t("tryAgain")}
      </button>
    </p>
  );
}

// vat_number/address are only required to ISSUE an invoice, not general company
// setup — so this is collected here, inline, the first time it's actually needed,
// rather than in onboarding.
function CompanyInfoGate({ seller, onSave }: { seller: Seller; onSave: (fields: SellerProfileUpdate) => Promise<void> }) {
  const t = useTranslations("consultantInvoiceCreate");
  const [street, setStreet] = useState(seller.addressStreet);
  const [postalCode, setPostalCode] = useState(seller.addressPostalCode);
  const [city, setCity] = useState(seller.addressCity);
  const [vatNumber, setVatNumber] = useState(seller.vatNumber);

  const needsVatNumber = seller.vatRegistered;
  const canSave = Boolean(street.trim() && postalCode.trim() && city.trim() && (!needsVatNumber || vatNumber.trim()));

  return (
    <GateAlert
      title={t("gate.companyTitle")}
      body={needsVatNumber ? t("gate.companyNeedsVat") : t("gate.companyNeedsAddress")}
      canSave={canSave}
      saveLabel={t("gate.saveCompany")}
      onSave={() => onSave({
        addressStreet: street.trim(), addressPostalCode: postalCode.trim(), addressCity: city.trim(),
        ...(needsVatNumber ? { vatNumber: vatNumber.trim() } : {}),
      })}
    >
      {needsVatNumber && (
        <GateField wide label={t("gate.vatNumber")} value={vatNumber} onChange={setVatNumber} />
      )}
      <GateField wide label={t("gate.street")} value={street} onChange={setStreet} />
      <GateField label={t("gate.postalCode")} value={postalCode} onChange={setPostalCode} />
      <GateField label={t("gate.city")} value={city} onChange={setCity} />
    </GateAlert>
  );
}

function GateField({ label, value, onChange, wide, placeholder }: {
  label: string; value: string; onChange: (value: string) => void; wide?: boolean; placeholder?: string;
}) {
  return (
    <div className={wide ? "col-span-2" : undefined}>
      <Label className="mb-1.5 block">{label} <span className="text-destructive">*</span></Label>
      <Input aria-label={label} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// Shared shell of the inline "collect this before you can issue" gates: alert
// frame, field grid, save button with pending state and the failure sentence.
function GateAlert({ title, body, canSave, saveLabel, onSave, children }: {
  title: string; body: string; canSave: boolean; saveLabel: string; onSave: () => Promise<void>; children: ReactNode;
}) {
  const t = useTranslations("consultantInvoiceCreate");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await onSave();
    } catch (err) {
      setError(userFacingErrorMessage(err, t("errors.generic")));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p className="mb-3">{body}</p>
        <div className="grid grid-cols-2 gap-3">{children}</div>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <Button size="sm" className="mt-3" disabled={!canSave || saving} onClick={submit}>
          {saving ? t("gate.saving") : saveLabel}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// A bank account (real connection, or a manually-entered one) is required to
// issue an invoice (issue #966) — collected inline here the first time it's
// actually needed, same pattern as CompanyInfoGate. Format-only validation:
// clearing number 4-5 digits, account number 2-10 digits — no per-bank
// clearing-range table (see the backend's validateManualBankAccountInput).
function BankAccountGate({ onSave }: { onSave: (input: ManualBankAccountInput) => Promise<void> }) {
  const t = useTranslations("consultantInvoiceCreate");
  const [clearingNumber, setClearingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const canSave = /^\d{4,5}$/.test(clearingNumber.trim()) && /^\d{2,10}$/.test(accountNumber.trim());

  return (
    <GateAlert
      title={t("gate.bankTitle")}
      body={t("gate.bankBody")}
      canSave={canSave}
      saveLabel={t("gate.saveBank")}
      onSave={() => onSave({ clearingNumber: clearingNumber.trim(), accountNumber: accountNumber.trim() })}
    >
      <GateField label={t("gate.clearing")} placeholder="3300" value={clearingNumber} onChange={setClearingNumber} />
      <GateField label={t("gate.account")} placeholder="1234567" value={accountNumber} onChange={setAccountNumber} />
    </GateAlert>
  );
}
